import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Link2, Mail, Send, LogOut } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const when = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };

// Roster → profile drawer: has this resident set up the portal, and the tools to get them in.
export function PortalAccessCard({ tenantId, person, readOnly }) {
  const [st, setSt] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState("");
  const [link, setLink] = useState("");
  const load = () => api.get(`/tenants/${tenantId}/people/${person.id}/portal-access`).then((r) => { setSt(r.data); setFailed(false); }).catch(() => { setSt(null); setFailed(true); });
  useEffect(() => { setLink(""); load(); }, [person?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (channel) => {
    setBusy(channel);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/people/${person.id}/portal-link`, { channel });
      setLink(data.link);
      if (channel === "copy") { try { await navigator.clipboard?.writeText(data.link); } catch { /* shown below */ } toast.success("Link copied — good for 7 days"); }
      else if (data.sent) toast.success(channel === "sms" ? "Texted them the link" : "Emailed them the link");
      else toast.warning(`Link created but not delivered — ${data.note || "delivery is off in this environment"}. Copy it below instead.`);
      load();
    } catch (e) { toast.error(errMsg(e, "Couldn't create a link")); }
    finally { setBusy(""); }
  };
  const signOut = async () => {
    setBusy("out");
    try { const { data } = await api.post(`/tenants/${tenantId}/people/${person.id}/portal-signout`); toast.success(`Signed out on ${data.sessions_ended} device${data.sessions_ended === 1 ? "" : "s"}`); load(); }
    catch (e) { toast.error(errMsg(e, "Couldn't sign them out")); }
    finally { setBusy(""); }
  };

  const first = (person?.name || "them").split(" ")[0];
  return (
    <div data-testid="pf-portal-access" className="rounded-2xl border border-sand-200 bg-white px-4 py-3 space-y-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 font-semibold text-ink"><KeyRound className="h-4 w-4 text-redline-500" /> Portal sign-in</span>
        {failed && <span data-testid="pf-portal-status" className="text-xs font-semibold text-ink/50">Couldn't load status</span>}
        {st && (
          st.has_password
            ? <span data-testid="pf-portal-status" className="text-xs font-semibold text-emerald-700">Activated {when(st.activated_at)}{st.active_sessions ? ` · ${st.active_sessions} device${st.active_sessions === 1 ? "" : "s"}` : ""}</span>
            : <span data-testid="pf-portal-status" className="text-xs font-semibold text-amber-700">Not activated yet</span>
        )}
      </div>
      <p className="text-[11px] text-ink/50">
        {st?.has_password
          ? `${first} signs in with ${[person.phone, person.email].filter(Boolean).join(" or ") || "their contact on file"} and a password. Send a link if they've forgotten it.`
          : `Send ${first} a link to set their password${st?.account_number ? ` — or read them account number ${st.account_number}; with their last name it activates from the sign-in page.` : "."}`}
      </p>
      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          {person.phone && <button data-testid="pf-portal-text-link" disabled={!!busy} onClick={() => send("sms")} className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 px-3 py-1.5 text-xs font-semibold hover:border-ink transition-colors disabled:opacity-50"><Send className="h-3.5 w-3.5" /> Text link</button>}
          {person.email && <button data-testid="pf-portal-email-link" disabled={!!busy} onClick={() => send("email")} className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 px-3 py-1.5 text-xs font-semibold hover:border-ink transition-colors disabled:opacity-50"><Mail className="h-3.5 w-3.5" /> Email link</button>}
          <button data-testid="pf-portal-copy-link" disabled={!!busy} onClick={() => send("copy")} className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 px-3 py-1.5 text-xs font-semibold hover:border-ink transition-colors disabled:opacity-50"><Link2 className="h-3.5 w-3.5" /> Copy link</button>
          {st?.has_password && <button data-testid="pf-portal-signout" disabled={!!busy} onClick={signOut} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-ink/60 hover:text-danger-600 transition-colors disabled:opacity-50"><LogOut className="h-3.5 w-3.5" /> Sign out everywhere</button>}
        </div>
      )}
      {link && (
        <div className="flex items-center gap-2 bg-sand-50 border border-sand-200 rounded-xl px-3 py-2">
          <input readOnly data-testid="pf-portal-link" value={link} className="flex-1 bg-transparent text-xs outline-none" />
          <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Copied"); }} className="text-xs font-semibold text-redline-600 shrink-0">Copy</button>
        </div>
      )}
    </div>
  );
}
